// packages/core/src/learning/schools/rich-life-ramit.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Rich life (Ramit)". Matches boglehead-indexing.ts
// gold standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview names: ramit-sethi (sole figure; this is
//                     almost certainly ramit-sethi's primary home school).
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const RICH_LIFE_RAMIT_EXHIBIT: SchoolExhibitEntry = {
	id: 'rich-life-ramit',
	tagline: 'Rich life believes money should fund what you actually value.',
	worldviewSummary:
		'Rich life is paying attention to the gap between looking responsible and living intentionally. This lens protects you from budgets that only create guilt, spending plans that ignore joy, and money habits that run on willpower alone. It accepts a real tradeoff: spending boldly on what matters means cutting clearly from what does not. The blind spot is that this lens can become luxury worship if values, income, debt, and long term stability are not kept in view. At its best, Rich life is not about copying someone else’s dream. It is about building a money system around the life you would actually choose.',
	coreBeliefs: [
		'Rich life believes money should serve your values, not random guilt.',
		'Rich life believes conscious spending works better than vague restriction.',
		'Rich life believes automation can protect good decisions before emotions interfere.',
		'Rich life believes earning more can be a serious lever, not something to ignore.',
		'Rich life believes big wins often matter more than obsessing over tiny cuts.',
		'Rich life believes your rich life has to be personal, not copied from someone else.',
	],
	warnsAgainst: [
		'Budgeting that only makes people feel guilty.',
		'Cutting everything fun while still avoiding the big money decisions.',
		'Calling every luxury a rich life without checking the real tradeoff.',
		'Letting automation replace attention completely.',
	],
	helps: 'This lens helps people who want money to feel intentional, useful, and connected to the life they actually want. It may not fit when spending values become an excuse to ignore debt, unstable income, weak savings, or long term tradeoffs.',
	figures: [],
	canon: [
		{
			title: 'I Will Teach You To Be Rich',
			author: 'Ramit Sethi',
			oneLineIdea:
				'A money system can automate the basics while leaving room for spending on what matters.',
		},
		{
			title: 'Conscious Spending Plan',
			author: 'Ramit Sethi',
			oneLineIdea:
				'Spend clearly on priorities instead of relying on shame driven budgeting.',
		},
		{
			title: 'Money Dials',
			author: 'Ramit Sethi',
			oneLineIdea:
				'People have different spending joys, and the point is to fund yours on purpose.',
		},
		{
			title: 'Big wins',
			oneLineIdea:
				'Large decisions around housing, income, debt, investing, and recurring costs often matter more than tiny guilt.',
		},
		{
			title: 'Automation first',
			oneLineIdea:
				'A strong system makes good money behavior easier before motivation gets tested.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-rich-life-ramit',
	debateIds: [],
	applyActions: [
		{
			label: 'Check whether your money supports what you value',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Build a freedom plan around your real priorities',
			action: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
		},
		{
			label: 'Test how more income changes your options',
			action: {
				type: 'openSimulator',
				label: 'Simulate more income',
				scenarioType: 'side-hustle',
			},
		},
		{
			label: 'Name the spending that actually feels worth it',
			action: {
				type: 'noToolAction',
				label: 'Name your money dials',
				prompt: 'Write three spending areas that genuinely improve your life, then write three that mostly drain money without adding much value.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Rich life asks a sharper question: what is your money actually for?',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of guilt budgeting is never learning what you truly value.',
			kind: 'hook',
		},
		{
			line: 'What do you spend on that feels expensive to others but deeply worth it to you?',
			kind: 'interview-question',
		},
		{
			line: 'Ask someone what they stopped feeling guilty about once their money system got stronger.',
			kind: 'creator-prompt',
		},
		{
			line: 'Nisk helps users learn the Rich life lens, then test the tradeoffs on their own numbers.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience why cutting lattes is not a money plan by itself.',
			kind: 'creator-prompt',
		},
		{
			line: 'Where does intentional spending help, and where does it become lifestyle creep in disguise?',
			kind: 'interview-question',
		},
		{
			line: 'A budget that ignores joy usually does not survive real life.',
			kind: 'hook',
		},
	],
	revision: 1,
};
